// Speech recognition for dictation, via the Web Speech API's
// SpeechRecognition (prefixed as webkitSpeechRecognition in Chrome/Edge).
//
// The counterpart of tts.ts: that module speaks a sentence, this one listens
// for the user repeating it back. Feature-detected throughout — Firefox does
// not implement SpeechRecognition at all, and callers are expected to hide
// their entry point when `isSupported()` is false rather than fail at click
// time.
//
// PRIVACY: unlike every other part of this app, this is not local. Chrome and
// Edge stream microphone audio to their vendor's speech service. Nothing else
// here touches the network at runtime.

// Minimal structural types. lib.dom's SpeechRecognition definitions vary by
// TypeScript version and the API is still prefixed in shipping browsers, so
// declaring the slice we use keeps this compiling everywhere.
interface SpeechAlternative {
  transcript: string;
  confidence: number;
}
interface SpeechResult {
  length: number;
  isFinal: boolean;
  [index: number]: SpeechAlternative;
}
interface SpeechResultList {
  length: number;
  [index: number]: SpeechResult;
}
interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: SpeechResultList;
}
interface SpeechRecognitionErrorEventLike {
  error: string;
}
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onerror: ((e: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function getCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** True when this browser can do speech recognition at all (not Firefox). */
export function isSupported(): boolean {
  return getCtor() !== null;
}

/**
 * Why more than one candidate: recognisers return a ranked list, and for short
 * phrases the correct reading is often not ranked first. Checking every
 * alternative turns a lot of spurious "wrong" answers into correct ones.
 */
const MAX_ALTERNATIVES = 3;

export type AsrErrorCode =
  | 'not-allowed'
  | 'no-speech'
  | 'audio-capture'
  | 'network'
  | 'aborted'
  | 'unknown';

interface ListenOpts {
  /** Every alternative the engine returned, best-ranked first. */
  onResult: (alternatives: string[]) => void;
  onError: (code: AsrErrorCode) => void;
  /** Always fires when the session ends, after onResult/onError. */
  onEnd: () => void;
  onStart?: () => void;
  lang?: string;
}

function normalizeError(raw: string): AsrErrorCode {
  switch (raw) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'not-allowed';
    case 'no-speech':
      return 'no-speech';
    case 'audio-capture':
      return 'audio-capture';
    case 'network':
      return 'network';
    case 'aborted':
      return 'aborted';
    default:
      return 'unknown';
  }
}

/**
 * Listen for a single utterance.
 *
 * Returns a canceller. Calling it stops the session; `onEnd` still fires, but
 * no further `onResult` will be delivered.
 */
export function listen(opts: ListenOpts): () => void {
  const Ctor = getCtor();
  if (!Ctor) {
    opts.onError('unknown');
    opts.onEnd();
    return () => {};
  }

  const rec = new Ctor();
  rec.lang = opts.lang ?? 'en-US';
  rec.continuous = false; // one sentence per press
  rec.interimResults = false; // only settled text
  rec.maxAlternatives = MAX_ALTERNATIVES;

  let cancelled = false;
  let finished = false;

  const finish = () => {
    if (finished) return;
    finished = true;
    opts.onEnd();
  };

  rec.onstart = () => {
    if (!cancelled) opts.onStart?.();
  };

  rec.onresult = (e) => {
    if (cancelled) return;
    const result = e.results[e.results.length - 1];
    if (!result) return;
    const alternatives: string[] = [];
    for (let i = 0; i < result.length; i++) {
      const text = result[i]?.transcript?.trim();
      if (text) alternatives.push(text);
    }
    if (alternatives.length) opts.onResult(alternatives);
  };

  rec.onerror = (e) => {
    if (cancelled) return;
    const code = normalizeError(e.error);
    // A user-initiated stop surfaces as 'aborted'; that is not a failure.
    if (code !== 'aborted') opts.onError(code);
  };

  rec.onend = () => finish();

  try {
    rec.start();
  } catch (e) {
    // start() throws if a session is somehow already running.
    console.warn('[asr] start failed', e);
    opts.onError('unknown');
    finish();
    return () => {};
  }

  return () => {
    cancelled = true;
    try {
      rec.abort();
    } catch {
      // already stopped
    }
    finish();
  };
}
